import { Controller } from "@hotwired/stimulus";

/**
 * places-autocomplete
 *
 * Remplace le legacy jQuery `SearchAutocomplete` (hook global `.search-autocomplete`)
 * pour les pages qui veulent une intégration propre en Stimulus.
 *
 * Usage recommandé (wrapper autour de l'input + hidden fields) :
 *   <div data-controller="places-autocomplete">
 *     <input data-places-autocomplete-target="input" ... />
 *     <input data-places-autocomplete-target="latitude" ... />
 *     <input data-places-autocomplete-target="longitude" ... />
 *     <input data-places-autocomplete-target="region" ... />
 *     <input data-places-autocomplete-target="department" ... />
 *     <input data-places-autocomplete-target="cityId" ... />
 *   </div>
 *
 * Effets:
 * - Sur sélection d'un lieu, renseigne lat/lng + region/department (via GooglePlaceToRegionDepartment)
 * - Reset des champs si l'utilisateur vide l'input
 * - Déclenche change/input sur le <form> parent pour que api-sync envoie le payload
 */
export default class extends Controller {
  static targets = ["input", "latitude", "longitude", "region", "department", "cityId"];
  static values = {
    // Endpoint de résolution du city_id à partir des composants Google Places.
    // Optionnel : sans lui, on retombe sur l'ancien comportement synchrone.
    resolveUrl: String,
    // Délai de garde (ms) au-delà duquel on lance quand même la recherche même
    // si la résolution du city_id n'a pas répondu (filet anti-blocage, #491).
    resolveTimeout: { type: Number, default: 1500 },
  };

  connect() {
    this.autocomplete = null;
    this.onInputChange = () => this.handleManualClear();
    // Jeton anti-course : incrémenté à chaque sélection pour ignorer les
    // réponses de résolution périmées (l'utilisateur a resélectionné entre-temps).
    this.resolveToken = 0;
    this.resolveAbortController = null;

    if (this.hasInputTarget) {
      try { this.inputTarget.addEventListener("change", this.onInputChange); } catch (_) {}
    }

    this.initializeGooglePlaces();
  }

  disconnect() {
    try { this.inputTarget?.removeEventListener?.("change", this.onInputChange); } catch (_) {}
    try { if (this.resolveAbortController) this.resolveAbortController.abort(); } catch (_) {}
    if (this.resolveTimeoutId) { try { clearTimeout(this.resolveTimeoutId); } catch (_) {} }
    this.autocomplete = null;
  }

  initializeGooglePlaces() {
    if (!this.hasInputTarget) return;
    const g = (typeof window !== "undefined") ? window.google : null;
    if (!g || !g.maps || !g.maps.places || !g.maps.places.Autocomplete) return;

    try {
      this.autocomplete = new g.maps.places.Autocomplete(this.inputTarget);
      this.autocomplete.addListener("place_changed", () => this.placeChanged());
    } catch (_) {
      this.autocomplete = null;
    }
  }

  placeChanged() {
    if (!this.autocomplete) return;
    const place = this.autocomplete.getPlace();
    if (!place) return;

    const isCountry = (place.types && place.types[0] === "country");
    const lat = (!isCountry && place.geometry && place.geometry.location) ? place.geometry.location.lat() : "";
    const lng = (!isCountry && place.geometry && place.geometry.location) ? place.geometry.location.lng() : "";

    let region = "";
    let department = "";
    try {
      const helper = (typeof window !== "undefined") ? window.GooglePlaceToRegionDepartment : null;
      if (helper) {
        region = helper.getRegionIso(place) || "";
        department = helper.getDepartmentNumber(place) || "";
      }
    } catch (_) {}

    // On (re)positionne les champs cachés ; cityId repart vide, il sera peut-être
    // renseigné par la résolution asynchrone ci-dessous.
    this.setHiddenFields({ latitude: lat, longitude: lng, region, department, cityId: "" });

    // #502 : une sélection de RÉGION (region renseignée par `getRegionIso`,
    // aucun département via `getDepartmentNumber`) ne doit JAMAIS résoudre un
    // `invest_city_id` : sinon la ville la plus proche du centroïde de la région
    // (repli `City.resolve`) court-circuite le filtre région dans
    // `Program.in_location_or_region` (city_id prioritaire). On laisse donc
    // `invest_city_id` vide et on lance directement la recherche par région,
    // comme avant #495.
    const isRegionSelection = region !== "" && department === "";

    // #491 : quand on peut résoudre une ville (endpoint configuré + composants
    // exploitables), on DIFFÈRE la recherche jusqu'à ce que `invest_city_id`
    // soit connu, pour que la recherche parte avec le bon filtre `city_id`.
    const cityInfo = this.extractCity(place);
    if (!isRegionSelection && this.hasResolveUrlValue && this.resolveUrlValue && cityInfo && cityInfo.name && lat !== "" && lng !== "") {
      this.resolveCityIdThenDispatch({ ...cityInfo, latitude: lat, longitude: lng });
    } else {
      this.dispatchFormChange();
    }
  }

  // Extrait le nom de localité et le code postal des composants structurés du
  // lieu Google, avec repli sur `place.name` pour le nom.
  extractCity(place) {
    let name = "";
    let postcode = "";
    try {
      const components = (place && place.address_components) || [];
      components.forEach((component) => {
        const types = component.types || [];
        if (!name && (types.includes("locality") || types.includes("postal_town"))) {
          name = component.long_name || component.short_name || "";
        }
        if (!postcode && types.includes("postal_code")) {
          postcode = component.long_name || component.short_name || "";
        }
      });
    } catch (_) {}

    if (!name && place && place.name) name = place.name;
    if (!name) return null;
    return { name, postcode };
  }

  // Appelle l'endpoint de résolution puis, à la réponse (ou au timeout), écrit
  // `cityId` et déclenche UNE seule recherche. Un jeton anti-course garantit
  // qu'une réponse périmée (resélection rapide) est ignorée.
  resolveCityIdThenDispatch({ name, postcode, latitude, longitude }) {
    const token = ++this.resolveToken;

    // Empêche la mutation de l'input par Google de déclencher une recherche
    // parasite (api-sync ignore les événements issus d'un [data-api-sync-ignore]).
    this.beginPendingResolution();

    try { if (this.resolveAbortController) this.resolveAbortController.abort(); } catch (_) {}
    this.resolveAbortController = (typeof AbortController !== "undefined") ? new AbortController() : null;

    if (this.resolveTimeoutId) { try { clearTimeout(this.resolveTimeoutId); } catch (_) {} }
    const timeoutMs = typeof this.resolveTimeoutValue === "number" ? this.resolveTimeoutValue : 1500;
    this.resolveTimeoutId = setTimeout(() => {
      if (token !== this.resolveToken) return;
      try { if (this.resolveAbortController) this.resolveAbortController.abort(); } catch (_) {}
      this.finalizeResolution(token, "");
    }, timeoutMs);

    const params = new URLSearchParams();
    params.set("name", name || "");
    if (postcode) params.set("postcode", postcode);
    params.set("latitude", String(latitude));
    params.set("longitude", String(longitude));
    const url = `${this.resolveUrlValue}?${params.toString()}`;

    const options = { headers: { Accept: "application/json" }, credentials: "same-origin" };
    if (this.resolveAbortController) options.signal = this.resolveAbortController.signal;

    fetch(url, options)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((json) => {
        if (token !== this.resolveToken) return;
        const cityId = (json && json.city_id != null) ? json.city_id : "";
        this.finalizeResolution(token, cityId);
      })
      .catch((err) => {
        if (err && err.name === "AbortError") return;
        if (token !== this.resolveToken) return;
        this.finalizeResolution(token, "");
      });
  }

  // Écrit le city_id résolu et lance la recherche, une seule fois par jeton.
  finalizeResolution(token, cityId) {
    if (token !== this.resolveToken) return;
    if (this.resolveTimeoutId) { try { clearTimeout(this.resolveTimeoutId); } catch (_) {} this.resolveTimeoutId = null; }
    if (this.hasCityIdTarget) this.cityIdTarget.value = cityId == null ? "" : String(cityId);
    this.endPendingResolution();
    this.dispatchFormChange();
  }

  beginPendingResolution() {
    if (this.hasInputTarget) {
      try { this.inputTarget.setAttribute("data-api-sync-ignore", "true"); } catch (_) {}
    }
  }

  endPendingResolution() {
    if (this.hasInputTarget) {
      try { this.inputTarget.removeAttribute("data-api-sync-ignore"); } catch (_) {}
    }
  }

  handleManualClear() {
    if (!this.hasInputTarget) return;
    const v = (this.inputTarget.value || "").trim();
    if (v !== "") return;

    this.setHiddenFields({ latitude: "", longitude: "", region: "", department: "", cityId: "" });
    this.dispatchFormChange();
  }

  setHiddenFields({ latitude, longitude, region, department, cityId }) {
    if (this.hasLatitudeTarget) this.latitudeTarget.value = latitude == null ? "" : String(latitude);
    if (this.hasLongitudeTarget) this.longitudeTarget.value = longitude == null ? "" : String(longitude);
    if (this.hasRegionTarget) this.regionTarget.value = region == null ? "" : String(region);
    if (this.hasDepartmentTarget) this.departmentTarget.value = department == null ? "" : String(department);
    if (this.hasCityIdTarget) this.cityIdTarget.value = cityId == null ? "" : String(cityId);
  }

  dispatchFormChange() {
    // api-sync écoute input/change en capture sur le form
    const form = this.element.closest("form");
    if (!form) return;
    try { form.dispatchEvent(new Event("change", { bubbles: true })); } catch (_) {}
    try { form.dispatchEvent(new Event("input", { bubbles: true })); } catch (_) {}
  }
}

